const post = {
  slug: "price-products-on-instagram-india",
  title: "How to Price Products on Instagram: A Simple Margin Guide",
  seoTitle: "How to Price Products on Instagram",
  description:
    "Learn how to price products for your Instagram shop. Work out your true costs, set a margin with a worked rupee example and avoid discounts that erase your profit.",
  excerpt:
    "Many Instagram sellers price by copying a competitor and hoping for the best. Here is a simple way to work out your real costs, choose a margin and keep discounts from eating your profit.",
  category: "Selling on Instagram",
  tags: ["Price products on Instagram", "Pricing for small sellers", "Profit margin guide"],
  date: "2026-10-26",
  cover: {
    src: "/blog-images/price-products-on-instagram-india-cover.png",
    alt: "A phone showing an Instagram DM with a product price and a simple cost breakdown",
  },
  blocks: [
    {
      type: "p",
      text:
        "A price is not just a number on a post. It decides whether your shop makes money, whether customers see value and whether you can afford to keep going. Yet many small sellers set prices by looking at what others charge and subtracting a little.",
    },
    {
      type: "p",
      text:
        "This guide shows a simpler and safer way: start from your real costs, add a margin that pays you properly and then check the result against the market. There is a worked example in rupees, so you can follow the arithmetic with your own numbers.",
    },

    { type: "h2", text: "Start with every cost, not just the product" },
    {
      type: "p",
      text:
        "The most common pricing mistake is counting only what you paid for the product. Every order carries other costs, and they add up quickly. List all of them:",
    },
    {
      type: "ul",
      items: [
        "**Product cost.** What you pay to make or buy one item.",
        "**Packaging.** Boxes, mailers, tape, tags and inserts.",
        "**Shipping.** The average amount you pay the courier per order, if you offer free delivery.",
        "**Payment fees.** A gateway charges a percentage of each payment. Check your gateway\u2019s current rate.",
        "**Returns and damage.** Set aside a small allowance, because some items will come back or break.",
        "**Your own costs.** Tools, a share of ad spend and your time, even if it feels free.",
      ],
    },

    { type: "h2", text: "A worked example" },
    {
      type: "p",
      text:
        "Say you sell a shirt that costs you \u20B9400. You spend \u20B930 on packaging, and shipping costs you \u20B970 on average. For this illustration, assume a payment fee of 2% and a returns allowance of 5% of the price. Here is what happens at two different prices.",
    },
    {
      type: "table",
      headers: ["", "Priced at \u20B9999", "Priced at \u20B9699"],
      rows: [
        ["Product cost", "\u20B9400", "\u20B9400"],
        ["Packaging", "\u20B930", "\u20B930"],
        ["Shipping", "\u20B970", "\u20B970"],
        ["Payment fee (2%)", "\u20B920", "\u20B914"],
        ["Returns allowance (5%)", "\u20B950", "\u20B935"],
        ["**Total cost**", "**\u20B9570**", "**\u20B9549**"],
        ["**Profit per order**", "**\u20B9429**", "**\u20B9150**"],
        ["**Margin**", "**About 43%**", "**About 21%**"],
      ],
    },
    {
      type: "p",
      text:
        "The figures are illustrative, so use your own. The lesson holds anyway: dropping the price by \u20B9300 cut the profit by almost two thirds, because most of your costs do not shrink when the price does.",
    },

    { type: "h2", text: "Choose a margin on purpose" },
    {
      type: "p",
      text:
        "Margin is the share of the price you keep as profit. Pick a target and work backwards. If you want to keep about 40%, divide your costs by 0.6 as a rough guide. With costs of \u20B9570, that points to a price of about \u20B9950, which is close to the \u20B9999 above.",
    },
    {
      type: "p",
      text:
        "The right margin depends on your product and your volume. A handmade item that takes hours needs a higher margin than a simple reseller product that moves fast. What matters is that you chose the number deliberately instead of finding out later that you were working for free.",
    },

    { type: "h2", text: "Decide how to handle shipping" },
    {
      type: "p",
      text:
        "Customers love free delivery, and it can lift your conversion. But shipping is a real cost, so build it into the price rather than absorbing it from your profit. You have three common options:",
    },
    {
      type: "ul",
      items: [
        "**Free shipping on everything.** Simple, but make sure the price covers it.",
        "**A free shipping threshold.** Offering free delivery above a set order value encourages larger baskets.",
        "**Shipping charged separately.** Transparent, but customers sometimes drop off when they see an extra charge.",
      ],
    },

    { type: "h2", text: "Price with the customer\u2019s eyes" },
    {
      type: "ul",
      items: [
        "**End prices naturally.** Many sellers use endings like 499 or 999. Test what your audience responds to.",
        "**Show value, not just cost.** Photos, materials and reviews help a higher price feel fair.",
        "**Offer a bundle.** Two items for a slightly lower combined price raises your order value and your profit per order.",
        "**Offer tiers.** A basic and a premium version lets customers choose, and some will choose the better one.",
      ],
    },

    { type: "h2", text: "Use discounts without erasing your profit" },
    {
      type: "p",
      text:
        "Discounts are powerful and dangerous. A 20% discount does not cost you 20% of your profit. On a 40% margin, it costs you half of it. Before you offer a discount, check what the order still earns after the reduction.",
    },
    {
      type: "ul",
      items: [
        "**Prefer targeted offers.** A coupon for first-time buyers or for orders above a set value beats a blanket sale.",
        "**Protect a floor.** Decide the lowest price you will ever accept for each product.",
        "**Give something other than a lower price.** Free delivery or a small gift can feel generous while costing you less.",
      ],
    },
    {
      type: "callout",
      tone: "peach",
      title: "Check the maths before every sale",
      text:
        "Before you announce a discount, calculate the profit per order after it. If the number is too low to justify the work, change the offer, not your standards.",
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "Holding a price",
      caption: "Illustrative example. Use your own offer and wording.",
      messages: [
        { from: "buyer", text: "Can you give a discount on this shirt?" },
        { from: "shop", text: "My prices are fixed so I can keep the quality the same. But orders above \u20B91,500 get free delivery. Want to add one more item?" },
      ],
    },

    { type: "h2", text: "Check the market, but do not race to the bottom" },
    {
      type: "p",
      text:
        "Look at what similar sellers charge, but use it as information, not a rule. If your price is far below theirs, you may be leaving money on the table. If it is far above, you need a clear reason a customer would pay more, such as quality, speed or service. Selling at the lowest price in the market is a hard game for a small shop to win.",
    },

    { type: "h2", text: "Review your prices regularly" },
    {
      type: "p",
      text:
        "Costs change. Suppliers raise rates, courier charges shift and your own time gets more valuable. Look at your prices every few months and update them when the numbers no longer work. Your orders and sales in the dashboard show which products earn the most, so you know where to focus.",
    },
    {
      type: "p",
      text:
        "Pricing is also tied to how you sell. Our guides to [selling on Instagram DMs without losing orders](/blog/sell-on-instagram-dms-without-losing-orders) and [Instagram catalog photos and sizes](/blog/instagram-catalog-tips-photos-sizes) show how a clear catalog helps customers see the value behind your price. If you are just starting out, our guide to [selling on Instagram without a website](/blog/sell-on-instagram-without-a-website) covers the rest of the setup.",
    },
  ],
  related: [
    { to: "/catalog-guide", title: "Catalog Guide", description: "Add products, sizes and prices to your chats." },
    { to: "/discounts-guide", title: "Discounts Guide", description: "Set up coupons and offers that protect your margin." },
    { to: "/pricing", title: "Pricing", description: "Compare plans, from the Free plan to Pro." },
  ],
  faqs: [
    {
      q: "How do I price products for my Instagram shop?",
      a: "Add up every cost, including product, packaging, shipping, payment fees and a returns allowance. Then choose a target margin and work out the price that delivers it, and compare the result with similar sellers.",
    },
    {
      q: "What profit margin should a small seller aim for?",
      a: "It depends on your product and volume. Handmade or low-volume products usually need a higher margin than fast-moving resale items. Choose a target on purpose, and check that it pays you for your time.",
    },
    {
      q: "Should I offer free shipping?",
      a: "Free shipping often improves conversion, but it is a cost. Either build it into your prices, or offer it above a minimum order value so that larger orders cover it.",
    },
    {
      q: "Can I offer discount codes in Checkout DM?",
      a: "Yes, coupons and offers are available from the Growth plan. Check the pricing page for what each plan includes.",
    },
  ],
};

export default post;
